import React, { useState } from 'react';

export interface NewAdjustmentPayload {
  type: 'Discount' | 'Refund' | 'Extra Charge' | 'Penalty';
  related_to: string;
  amount: number;
  reason: string;
  notes?: string;
}

interface Props {
  onClose: () => void;
  onSubmit: (data: NewAdjustmentPayload) => Promise<boolean> | boolean;
  isSubmitting?: boolean;
}

const CreateAdjustmentModal: React.FC<Props> = ({
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const [type, setType] =
    useState<'Discount' | 'Refund' | 'Extra Charge' | 'Penalty'>('Discount');
  const [relatedTo, setRelatedTo] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    if (!relatedTo || !amount || !reason) {
      setError('Please fill all required fields.');
      return;
    }

    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      setError('Please enter a valid amount.');
      return;
    }

    const shouldClose = await onSubmit({
      type,
      related_to: relatedTo,
      amount:
        type === 'Extra Charge' || type === 'Penalty'
          ? Math.abs(value)
          : -Math.abs(value),
      reason,
      notes,
    });

    if (shouldClose !== false) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-3 sm:p-4">
      <div className="bg-white w-full max-w-lg rounded-lg shadow-xl flex flex-col max-h-[88vh]">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-base font-semibold text-gray-800">Create Adjustment</h3>
          <button
            onClick={onClose}
            className="h-7 w-7 inline-flex items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            x
          </button>
        </div>

        <div className="px-4 py-3 overflow-y-auto space-y-3 flex-1">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-xs">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-700">Adjustment Type</label>
              <select
                className="w-full mt-1 h-9 border rounded px-3 text-sm"
                value={type}
                onChange={(e) => setType(e.target.value as any)}
              >
                <option value="Discount">Discount</option>
                <option value="Refund">Refund</option>
                <option value="Extra Charge">Extra Charge</option>
                <option value="Penalty">Penalty</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-700">Amount</label>
              <input
                type="number"
                className="w-full mt-1 h-9 border rounded px-3 text-sm"
                placeholder="Enter amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-700">Related To</label>
            <input
              className="w-full mt-1 h-9 border rounded px-3 text-sm"
              placeholder="Service / Job / Booking"
              value={relatedTo}
              onChange={(e) => setRelatedTo(e.target.value)}
            />
          </div>

          <div>
            <label className="text-xs font-medium text-gray-700">Reason</label>
            <textarea
              className="w-full mt-1 border rounded px-3 py-2 text-sm min-h-[72px] resize-y"
              placeholder="Why is this adjustment added?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          <div>
            <label className="text-xs font-medium text-gray-700">Notes (Optional)</label>
            <textarea
              className="w-full mt-1 border rounded px-3 py-2 text-sm min-h-[64px] resize-y"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 px-4 py-3 border-t">
          <button
            onClick={onClose}
            className="h-9 px-3 text-sm border rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="h-9 px-3 text-sm bg-blue-600 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Adding...' : 'Add Adjustment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateAdjustmentModal;
